/* eslint-disable react/jsx-key */
import { lit, end, zero, parse, Route, Parser, format } from "fp-ts-routing";
import * as t from "io-ts";
import React, { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { NumberFromString } from "io-ts-types/lib/NumberFromString";
import GlobalErrorBoundary from "ui-components/global-error-boundary";
import NotFound from "./pages/not-found";

import SignUp from "./components/shared/sign-up";
import SignIn from "./components/shared/sign-in";
import DashboardPage from "./pages/dashboard";
import FundingRoundsPage from "./pages/funding-rounds";
// import { queryPartial } from "ui-io/query-partial";
import InvestmentTrends from "./pages/investment-trends";
import UnderDevelopment from "./components/shared/under-development";
import MyProfile from "./pages/profile";
import getDefaultDashboardProps from "./pages/dashboard/get-default-props";
import useSession from "ui-io/use-session";
import Signal from "./pages/signal";
import CompanyDetailsView from "./pages/company/details-view";
import { queryPartial } from "./utils/query-partial";

const fundingRoundsQuery = t.partial({
  page: NumberFromString,
  from: t.string,
  to: t.string,
  showFilter: t.boolean,
});

const companyQuery = t.partial({
  companyID: t.string,
});

const apiDocs = lit("api").then(lit("docs"));

const def = end;
const signUp = lit("sign-up").then(end);
const signIn = lit("sign-in").then(end);
const dashboard = lit("dashboard").then(end);
const fundingRounds = lit("funding-rounds").then(end);
const signal = lit("signal").then(end);
const investmentTrends = lit("investment-trends").then(end);
const investors = lit("investors").then(end);
const unicorns = lit("unicorns").then(end);
const people = lit("people").then(end);
const profile = lit("profile").then(end);
const companyDetails = lit("company-details").then(
  queryPartial(companyQuery)).then(end);
// const task = lit('tasks').then(
//   lit('inbox')
//     .then(lit('task'))
//     .then(str('taskId'))
//     .then(queryPartial(taskPageQuery))
//     .then(end),
// )

const match = {
  default: def,

  signUp,
  signIn,
  dashboard,
  fundingRounds,
	signal,
  investmentTrends,
  investors,
  unicorns,
  people,
  profile,
  companyDetails,
  apiDocs,
};

export type MatchT = keyof typeof match;

const withAuthentication = (
  router: Parser<ReactElement>
): Parser<ReactElement> => {
  return router
    .map((component) => {
      const { isAuthenticated } = useSession();
      // Exclude the sign-in route from authentication checks
      if (component.type === SignUp) {
        return component;
      } else if (component.type === SignIn) {
        if (isAuthenticated) {
          return (
            <Navigate
              to={formatLocation.dashboard(getDefaultDashboardProps())}
            />
          );
        } else {
          return component;
        }
      }
      return isAuthenticated ? (
        component
      ) : (
        <Navigate to={formatLocation.signIn({})} />
      );
    })
    .map((x) => (
      <>
        <ScrollToTop />
        <GlobalErrorBoundary>{x}</GlobalErrorBoundary>
      </>
    ));
};

const router = withAuthentication(
  zero<ReactElement>()
    .alt(
      match.default.parser.map(() => {
        const { isAuthenticated } = useSession();
        return (
          <Navigate
            to={
              isAuthenticated
                ? formatLocation.dashboard(getDefaultDashboardProps())
                : formatLocation.signIn({})
            }
          />
        );
      })
    )
    .alt(match.signUp.parser.map((props) => <SignUp {...props} />))
    .alt(match.signIn.parser.map((props) => <SignIn {...props} />))
    .alt(match.dashboard.parser.map((props) => <DashboardPage {...props} />))
    .alt(
      match.fundingRounds.parser.map((props) => (
        <FundingRoundsPage {...props} />
      ))
    )
    .alt(
      match.signal.parser.map((props) => (
        <Signal {...props} />
      ))
    )
    .alt(
      match.investmentTrends.parser.map((props) => (
        <InvestmentTrends {...props} />
      ))
    )
    .alt(
      match.investmentTrends.parser.map((props) => (
        <InvestmentTrends {...props} />
      ))
    )
    .alt(match.investors.parser.map((props) => <UnderDevelopment {...props} />))
    .alt(match.unicorns.parser.map((props) => <UnderDevelopment {...props} />))
    .alt(match.people.parser.map((props) => <UnderDevelopment {...props} />))
    .alt(match.companyDetails.parser.map((props) => <CompanyDetailsView {...props} />))
    .alt(match.profile.parser.map((props) => <MyProfile {...props} />))

    // .alt(match.apiDocs.parser.map(() => <ApiDocsPage />))
    .alt(Parser.of(<NotFound />))
);

const ScrollToTop = () => {
  // const history = useHistory()
  React.useEffect(() => {
    // if (history.action === 'PUSH') {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    // }
  }, []);
  return null;
};

// Parse location
export const parseLocation = (s: string) =>
  parse(router, Route.parse(s), <NotFound />);

export const formatLocation = Object.entries(match).reduce(
  (acc, [x, y]) => ({
    ...acc,
    // @ts-ignore
    [x]: (props: (typeof y)["_A"]) =>
      format<(typeof y)["_A"]>(y.formatter, props),
  }),
  {} as {
    [key in keyof typeof match]: (props: (typeof match)[key]["_A"]) => string;
  }
);

// const badTaskFormatter = formatLocation.task
// formatLocation.task = (props: Parameters<typeof badTaskFormatter>[0]) =>
//   cleanTaskUrl(badTaskFormatter(props))
